import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Library, Workflow, Dumbbell, Plus, ArrowRight, Play } from "lucide-react";
import { CATEGORY_COLORS } from "@/lib/constants";
import { getThumbnail } from "@/lib/video";

const HEADER = "https://images.unsplash.com/photo-1682545888368-587f56efd06e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [techniques, setTechniques] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [gym, setGym] = useState(null);

  useEffect(() => {
    api.get("/techniques").then(({ data }) => setTechniques(data)).catch(() => {});
    api.get("/sequences").then(({ data }) => setSequences(data)).catch(() => {});
    api.get("/gyms/me").then(({ data }) => setGym(data)).catch(() => {});
  }, []);

  const firstName = (user?.name || "Athlete").split(" ")[0];

  return (
    <div className="pb-24 md:pb-8">
      {/* Hero header */}
      <div className="relative h-44 sm:h-52">
        <img src={HEADER} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-black/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
          <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">{gym ? gym.name : "Welcome"}</p>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">Oss, {firstName}.</h1>
        </div>
      </div>

      <div className="p-6 md:p-8 space-y-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { label: "Techniques", value: techniques.length, icon: Library, to: "/library" },
            { label: "Sequences", value: sequences.length, icon: Workflow, to: "/sequences" },
            { label: "Gym", value: gym ? "Active" : "None", icon: Dumbbell, to: "/gym" },
          ].map((s) => (
            <button key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} onClick={() => navigate(s.to)}
              className="group text-left p-5 rounded-xl bg-card border border-border hover:border-primary/60 transition-colors">
              <div className="flex items-center justify-between">
                <s.icon className="h-5 w-5 text-primary" />
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="font-heading font-black text-3xl mt-4 tracking-tight">{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Quick actions */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-6 rounded-xl bg-gradient-to-br from-primary/15 to-card border border-primary/30">
            <Workflow className="h-7 w-7 text-primary mb-3" />
            <h3 className="font-heading font-semibold text-lg">Build a sequence</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Map your options after any position with a drag-and-drop flowchart.</p>
            <Button data-testid="dash-new-sequence-btn" onClick={() => navigate("/sequences/new")} className="bg-primary hover:bg-primary/90 text-white"><Plus className="h-4 w-4 mr-1.5" /> New sequence</Button>
          </div>
          <div className="p-6 rounded-xl bg-card border border-border">
            <Library className="h-7 w-7 text-primary mb-3" />
            <h3 className="font-heading font-semibold text-lg">Add a technique</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Document a move with details, tags and a video to grow your library.</p>
            <Button data-testid="dash-add-technique-btn" onClick={() => navigate("/library")} variant="outline" className="border-border"><Plus className="h-4 w-4 mr-1.5" /> Go to library</Button>
          </div>
        </div>

        <Link to="/how-it-works" data-testid="dash-how-it-works-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
          New here? Read how Infinite Sequence helps you learn faster <ArrowRight className="h-3.5 w-3.5" />
        </Link>

        {/* Recent techniques */}
        {techniques.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl">Recent techniques</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/library")} className="text-primary hover:text-primary h-10 sm:h-8">View all <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {techniques.slice(0, 4).map((t) => (
                <div key={t.id} data-testid={`dash-technique-${t.id}`} onClick={() => navigate(`/techniques/${t.id}`)}
                  className="group rounded-xl bg-card border border-border hover:border-primary/60 transition-colors overflow-hidden cursor-pointer">
                  <div className="relative aspect-video bg-black">
                    <img src={getThumbnail(t)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 flex items-center justify-center"><div className="h-9 w-9 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform"><Play className="h-4 w-4 text-white fill-white ml-0.5" /></div></div>
                  </div>
                  <div className="p-3">
                    {t.category && <span className="text-[10px] font-semibold" style={{ color: CATEGORY_COLORS[t.category] }}>{t.category}</span>}
                    <p className="font-heading font-semibold text-sm leading-tight truncate">{t.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
